import { useState, useEffect } from "react";
import { fetchTags } from "@/services/menu";
import { MenuTag, MenuItemFormData } from "@/types/menu";
import { filterValidCategoryIds } from "@/utils/categoryUtils";

interface UseMenuItemFormProps {
  initialData: {
    name: string;
    description: string;
    price: string;
    available: boolean;
    image_url?: string;
    store_id: string; // This must be required
    categoryIds: string[];
    tags: string[];
    availability_dates?: {
      from?: Date;
      to?: Date;
    };
    always_available?: boolean;
    available_quantity?: number;
  };
}

export function useMenuItemForm({ initialData }: UseMenuItemFormProps) {
  const [formData, setFormData] = useState<MenuItemFormData>({
    name: initialData.name,
    description: initialData.description,
    price: initialData.price,
    available: initialData.available,
    image_url: initialData.image_url,
    store_id: initialData.store_id,
    categoryIds: initialData.categoryIds,
    tags: initialData.tags,
    availability_dates: initialData.availability_dates,
    always_available: initialData.always_available,
    available_quantity: initialData.available_quantity,
  });
  const [uploadedImage, setUploadedImage] = useState<string | null>(
    initialData.image_url || null
  );
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [availableTags, setAvailableTags] = useState<MenuTag[]>([]);

  useEffect(() => {
    const loadTags = async () => {
      try {
        const tags = await fetchTags();
        setAvailableTags(tags);
      } catch (error) {
        console.error("Error loading tags:", error);
      }
    };

    loadTags();
  }, []);

  const handleChange = (
    field: keyof MenuItemFormData,
    value: string | boolean | string[] | object
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));

    // Clear error for the field when it's changed
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    }

    if (!formData.price.trim()) {
      newErrors.price = "Price is required";
    } else if (
      isNaN(parseFloat(formData.price)) ||
      parseFloat(formData.price) <= 0
    ) {
      newErrors.price = "Price must be a positive number";
    }

    if (!formData.store_id) {
      newErrors.store_id = "Store is required";
    }

    // Filter out "all" and invalid category IDs
    const validCategoryIds = filterValidCategoryIds(formData.categoryIds || []);

    if (validCategoryIds.length === 0) {
      newErrors.categoryIds = "At least one category is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const getFormData = () => {
    // Filter out "all" and invalid category IDs
    const validCategoryIds = filterValidCategoryIds(formData.categoryIds || []);

    // Return form data with uploaded image and clean category IDs
    return {
      ...formData,
      categoryIds: validCategoryIds,
      image_url: uploadedImage || formData.image_url || null,
    };
  };

  return {
    formData,
    errors,
    uploadedImage,
    availableTags,
    hasErrors: Object.keys(errors).length > 0,
    handleChange,
    setUploadedImage,
    validateForm,
    getFormData,
  };
}
